const BADGE_SELECTOR = "[data-nx-platform-badge]";
const STYLE_TAG_ID = "nx-branding-watchdog-style";

const FORCED_CSS = `
[data-nx-platform-badge],
.nx-platform-badge,
.nx-powered-by,
[data-nexus-branding] {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
  pointer-events: auto !important;
  position: static !important;
  clip: auto !important;
  clip-path: none !important;
  height: auto !important;
  width: auto !important;
  max-height: none !important;
  max-width: none !important;
  overflow: visible !important;
  transform: none !important;
  filter: none !important;
}
[data-nx-platform-badge] *,
.nx-platform-badge *,
.nx-powered-by * {
  visibility: visible !important;
  opacity: 1 !important;
  color: inherit !important;
}
`;

function ensureForcedStyle(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_TAG_ID;
  style.setAttribute("data-nx-locked", "1");
  style.textContent = FORCED_CSS;
  (document.head || document.documentElement).appendChild(style);
}

function reassertStyleNode(): void {
  const existing = document.getElementById(STYLE_TAG_ID);
  if (!existing || existing.tagName !== "STYLE") {
    if (existing) existing.remove();
    ensureForcedStyle();
    return;
  }
  if (!(existing as HTMLStyleElement).textContent?.includes("data-nx-platform-badge")) {
    (existing as HTMLStyleElement).textContent = FORCED_CSS;
  }
  if (existing.parentNode !== document.head && existing.parentNode !== document.documentElement) {
    (document.head || document.documentElement).appendChild(existing);
  }
}

let installed = false;

/**
 * Runtime self-heal watchdog for the platform attribution badge.
 *
 * The server-side branding lock blocks custom CSS/JS from mentioning the
 * known platform anchors, but a tenant with custom-code privileges could
 * still hide attribution via STRUCTURAL selectors that don't mention any
 * anchor (e.g. `footer > div:last-child{display:none}`). This watchdog
 * defends client-side by:
 *
 *   1. Injecting a high-priority `<style>` tag with `!important` overrides
 *      that force the badge to remain visible regardless of any other CSS.
 *   2. Observing `<head>`, `<body>`, and the badge container for mutations.
 *      If the override style tag is removed, it is re-injected. If the
 *      badge element is detached, the observer triggers and a re-mount
 *      pass is requested via the React hook in StoreFooter.
 *
 * The watchdog is install-once and safe to call from multiple components.
 */
export function installBrandingWatchdog(): void {
  if (installed) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  installed = true;

  ensureForcedStyle();

  const observer = new MutationObserver(() => {
    reassertStyleNode();
  });

  const start = () => {
    try {
      observer.observe(document.head || document.documentElement, {
        childList: true,
        subtree: false,
      });
      if (document.body) {
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["style", "class", "hidden"],
        });
      }
    } catch {
      /* ignore observer setup errors in non-DOM environments */
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }

  window.setInterval(() => {
    try {
      reassertStyleNode();
      const badge = document.querySelector(BADGE_SELECTOR);
      if (badge instanceof HTMLElement) {
        const cs = window.getComputedStyle(badge);
        if (cs.display === "none" || cs.visibility === "hidden" || parseFloat(cs.opacity) < 0.1) {
          badge.style.setProperty("display", "flex", "important");
          badge.style.setProperty("visibility", "visible", "important");
          badge.style.setProperty("opacity", "1", "important");
        }
      }
    } catch {
      /* swallow — watchdog must never crash */
    }
  }, 2000);
}
